import Language from "../../app/models/Language";
import Template from "../../app/models/Template";
import {
  sendBlastMessage,
  sendCreateTemplate,
  sendMessage,
  sendUpdateTemplate,
} from "./connection";
import * as Log from "src/util/SDayslogger"

interface ISendRequestBlastMessage { 
  responseCode : string; 
  responseJson: {
    resultCode: string;
    message: string;
  };
}

export const sendRequestMessage = async (
  type: string,
  company: string,
  from: string,
  to: string,
  templateName: string,
  templateLanguage: string,
  bodyParameters: Array<string>,
  callback: any
) => {
  console.log("sendRequestMessage");
  const component = () => {
    let jsonComponent = ``;
    //if any parameter found
    if (bodyParameters.length > 0) {
      jsonComponent = `
      , "components" : [
        <<component>>
      ]
      `;
    }
    //TODO: Add more components for header

    //
    if (bodyParameters.length > 0) {
      jsonComponent = jsonComponent.replace(
        "<<component>>",
        `{
        "type" : "body",
        "parameters" : [
          ${bodyParameters.map((parameter) => `"${parameter}"`).join(",")}
        ]
      }, <<component>>`
      );
    }
    jsonComponent = jsonComponent.replace(", <<component>>", "");
    return jsonComponent;
  };

  const jsonMessage = `
  {
    "type" : "${type}",
    "${type}" : {
      "company" : "${company}",
      "from" : "${from}",
      "to" : "${to}",
      "type" : "template",
      "template" : {
        "name" : "${templateName}",
        "language" : "${templateLanguage}"
        ${component()}
      }
    }
  }`;

  // console.log(jsonMessage);

  return await sendMessage(
    jsonMessage,
    (responseCode: string, responseJson: JSON) => {
      callback(responseCode, responseJson);
    }
  );
};

export const sendRequestBlastMessage = async (
  uid: string,
  companyID: string,
  accountID: string,
  campaignID: string,
  language: string,
  // bodyParameters: Array<string>,
  // callback: any
) : Promise<ISendRequestBlastMessage>  => {
  Log.info("sendRequest Blast Message");

  const jsonMessage = {
    uid: uid,
    company: companyID,
    account: accountID,
    campaign: campaignID,
    language: language,
  };

  let responseCode: string = '';
  let responseJson: any;

  await sendBlastMessage(
    jsonMessage,
    (code: string, data: JSON) => {
      responseCode = code;
      responseJson = data;
    }
  );

  return {responseCode, responseJson} as ISendRequestBlastMessage;
};

// Helper functions for Meta WhatsApp policy validation
const validateVariableLength = (value: string, fieldName: string): string[] => {
  const errors: string[] = [];
  const MAX_LENGTH = 15;
  if (value && value.length > MAX_LENGTH) {
    errors.push(`${fieldName} exceeds 15-character limit (${value.length} characters). WhatsApp restricts authentication template variables to 15 characters maximum.`);
  }
  return errors;
};

const validateNoLinks = (content: string, fieldName: string): string[] => {
  const errors: string[] = [];
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.(com|org|net|edu|gov|mil|int|co|io|ai|app|dev|tech|info|biz|name|pro)[^\s]*)/gi;
  if (content && urlRegex.test(content)) {
    errors.push(`${fieldName} contains links which are prohibited in authentication templates.`);
  }
  return errors;
};

const validateNoEmojis = (content: string, fieldName: string): string[] => {
  const errors: string[] = [];
  const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
  if (content && emojiRegex.test(content)) {
    errors.push(`${fieldName} contains emojis which are prohibited in authentication template variables.`);
  }
  return errors;
};

// Sanitize language data for Meta WhatsApp API submission
const sanitizeLanguageForMetaAPI = (language: Language, template: Template): Language => {
  console.log(`[Template Sanitization] Starting sanitization for Meta API`);
  
  // Create a deep copy to avoid modifying original
  const sanitized = JSON.parse(JSON.stringify(language));
  
  // Check if this is an OTP authentication template
  const isOTPTemplate = template.category?.toLowerCase() === "authentication" && 
    (language.addSecurityRecommendation || language.code_expiration_minutes);
  
  console.log(`[Template Sanitization] Is OTP template: ${isOTPTemplate}`);
  
  if (isOTPTemplate) {
    console.log(`[Template Sanitization] Sanitizing OTP template for Meta API`);
    
    // Remove failover configuration (internal SMS backup functionality)
    if (sanitized.failover) {
      console.log(`[Template Sanitization] Removing failover configuration from OTP template`);
      console.log(`[Template Sanitization] Original failover:`, sanitized.failover);
      delete sanitized.failover;
    }
    
    // Remove internal tracking fields that Meta API doesn't need
    const internalFields = ['errorMessage', 'responseJSON', 'status', 'createdAt', 'updatedAt', 'isActive'];
    internalFields.forEach(field => {
      if (sanitized[field]) {
        console.log(`[Template Sanitization] Removing internal field: ${field}`);
        delete sanitized[field];
      }
    });
    
    // For OTP with security recommendation, ensure no custom body/footer
    if (sanitized.addSecurityRecommendation) {
      console.log(`[Template Sanitization] OTP with security recommendation - removing custom content`);
      if (sanitized.body) {
        console.log(`[Template Sanitization] Removing custom body text`);
        delete sanitized.body;
      }
      if (sanitized.footer) {
        console.log(`[Template Sanitization] Removing custom footer text`);
        delete sanitized.footer;
      }
      if (sanitized.bodyParam) {
        console.log(`[Template Sanitization] Removing custom body parameters`);
        delete sanitized.bodyParam;
      }
    }
  } else if (template.category?.toLowerCase() === "authentication") {
    console.log(`[Template Sanitization] Regular authentication template - keeping failover for SMS backup`);
  } else {
    console.log(`[Template Sanitization] Non-authentication template - standard sanitization`);
  }
  
  console.log(`[Template Sanitization] Sanitization completed`);
  return sanitized;
};

// Client-side validation for authentication templates
const validateAuthenticationTemplate = (template: Template, language: Language): string[] => {
  const errors: string[] = [];
  
  console.log(`[OTP Validation] Starting validation for template category: ${template.category}`);
  console.log(`[OTP Validation] Language data:`, JSON.stringify(language, null, 2));
  
  // Check if this is an authentication template
  if (template.category?.toLowerCase() === "authentication") {
    const isOTPTemplate = language.addSecurityRecommendation || language.code_expiration_minutes;
    console.log(`[OTP Validation] Is OTP template: ${isOTPTemplate}`);
    
    // For OTP templates with security recommendation, the structure should be minimal
    if (language.addSecurityRecommendation) {
      console.log(`[OTP Validation] Validating OTP template with security recommendation`);
      
      // OTP templates with addSecurityRecommendation should not have custom body or footer text
      if (language.body && language.body.trim() !== '') {
        console.log(`[OTP Validation] ERROR: Custom body text found in OTP template: ${language.body}`);
        errors.push('OTP Authentication template body is auto-generated when using security recommendation. Custom body text should not be present.');
      }
      
      if (language.footer && typeof language.footer === 'string' && language.footer.trim() !== '') {
        console.log(`[OTP Validation] ERROR: Custom footer text found in OTP template: ${language.footer}`);
        errors.push('OTP Authentication templates with security recommendation cannot have custom footer text.');
      }
      
      // Should not have bodyParam for OTP templates with security recommendation
      if (language.bodyParam && language.bodyParam.length > 0) {
        console.log(`[OTP Validation] ERROR: Body parameters found in OTP template:`, language.bodyParam);
        errors.push('OTP Authentication templates with security recommendation should not have custom body parameters.');
      }
      
      // Should not have failover for OTP templates
      if (language.failover) {
        console.log(`[OTP Validation] ERROR: Failover configuration found in OTP template:`, language.failover);
        errors.push('OTP Authentication templates should not have failover configuration.');
      }
    } else {
      // For non-OTP authentication templates, apply different validation
      console.log(`[OTP Validation] Validating non-OTP authentication template`);
      
      // Check for invalid footer text only for non-OTP
      if (language.footer && typeof language.footer === 'string' && language.footer.trim() !== '') {
        // For non-OTP authentication templates, footer might be allowed
        console.log(`[OTP Validation] Custom footer found in non-OTP authentication template: ${language.footer}`);
      }
      
      // Check for invalid body text (should be auto-generated for OTP)
      if (language.body && language.body.trim() !== '') {
        // For non-OTP authentication templates, check for links
        errors.push(...validateNoLinks(language.body, 'Body text'));
      }
    }
    
    // Validate button constraints for authentication templates
    if (language.buttons && language.buttons.length > 0) {
      language.buttons.forEach((button, index) => {
        console.log(`[OTP Validation] Validating button ${index + 1}:`, button);
        
        // Check button text length (15-character limit)
        if (button.text) {
          errors.push(...validateVariableLength(button.text, `Button ${index + 1} text`));
          errors.push(...validateNoEmojis(button.text, `Button ${index + 1} text`));
          errors.push(...validateNoLinks(button.text, `Button ${index + 1} text`));
        }
        
        // For OTP templates, OTP button types are valid
        if (isOTPTemplate && button.type && !['OTP', 'COPY_CODE'].includes(button.type)) {
          console.log(`[OTP Validation] ERROR: Invalid button type for OTP template: ${button.type}`);
          errors.push(`OTP Authentication templates can only have OTP or COPY_CODE button types. Button ${index + 1} type "${button.type}" is not allowed.`);
        }
      });
    }
  }
  
  console.log(`[OTP Validation] Validation completed. Errors found: ${errors.length}`);
  if (errors.length > 0) {
    console.log(`[OTP Validation] Validation errors:`, errors);
  }
  
  return errors;
};

export const sendRequestCreateTemplate = async (
  headerFormat: string | undefined,
  language: Language,
  template: Template,
  whatsappBusinessID: string,
  whatsappAccessToken: string,
  isUpdateStage: boolean,
  templateID: string,
  callback: any
) => {
  console.log(`[Template Request] Starting sendRequestCreateTemplate`);
  console.log(`[Template Request] Header format: ${headerFormat}`);
  console.log(`[Template Request] Template category: ${template.category}`);
  console.log(`[Template Request] Is update stage: ${isUpdateStage}`);
  console.log(`[Template Request] WhatsApp Business ID: ${whatsappBusinessID}`);
  console.log(`[Template Request] Template ID: ${templateID}`);
  console.log(`[Template Request] Original language data:`, JSON.stringify(language, null, 2));
  
  // Create sanitized copy for Meta WhatsApp API (remove internal-only fields)
  const sanitizedLanguage = sanitizeLanguageForMetaAPI(language, template);
  console.log(`[Template Request] Sanitized language data for Meta API:`, JSON.stringify(sanitizedLanguage, null, 2));
  
  // Check for potential template naming conflicts
  const checkTemplateNamingConflicts = (templateName: string, category: string): string[] => {
    const errors: string[] = [];
    
    // Check for common conflicting names based on the current error
    const conflictingNames = {
      'authentication_code': 'UTILITY',
      'otp_code': 'UTILITY', 
      'verification_code': 'UTILITY',
      'auth_code': 'UTILITY'
    };
    
    const conflictingCategory = conflictingNames[templateName.toLowerCase() as keyof typeof conflictingNames];
    if (conflictingCategory && conflictingCategory !== category.toUpperCase()) {
      errors.push(`Template name "${templateName}" already exists with ${conflictingCategory} category. Cannot create with ${category.toUpperCase()} category. Please use a different name or consider using category prefix like "auth_${templateName}_${Date.now().toString().slice(-6)}".`);
    }
    
    return errors;
  };
  
  // Check for naming conflicts first
  const namingConflicts = checkTemplateNamingConflicts(template.templateName, template.category);
  if (namingConflicts.length > 0) {
    console.error(`[Template Request] Template naming conflict detected:`, namingConflicts);
    callback("", "", namingConflicts.join('; '), { 
      error: { 
        message: namingConflicts.join('; '),
        type: 'naming_conflict_error'
      } 
    });
    return;
  }
  
  // Validate authentication templates before submission
  const validationErrors = validateAuthenticationTemplate(template, sanitizedLanguage);
  if (validationErrors.length > 0) {
    console.error(`[Template Request] Authentication template validation failed:`, validationErrors);
    console.error(`[Template Request] Template will not be sent to server due to validation errors`);
    // Call callback with error
    callback("", "", validationErrors.join('; '), { 
      error: { 
        message: validationErrors.join('; '),
        type: 'validation_error'
      } 
    });
    return;
  }
  
  console.log(`[Template Request] Validation passed, proceeding with template creation`);

  // let isUpdateStage = true;

  let templateHeaderText = ``;

  if (
    language.header?.text &&
    language.header.text !== "" &&
    language.header.text !== "undefined"
  ) {
    templateHeaderText = `,{"type" : "HEADER",
      "format" : "${headerFormat}",
      "text" : "${language.header?.text}"
      <<example>>
    }`;
  }

  let templateHeaderMedia = ``;

  // Use sanitized language for template creation
  const workingLanguage = sanitizedLanguage;
  console.log(`[Template Request] Using sanitized language for template creation`);
  
  if (
    headerFormat &&
    headerFormat !== "" &&
    headerFormat !== "undefined" &&
    workingLanguage.responseUploadMediaID &&
    workingLanguage.responseUploadMediaID !== "" &&
    workingLanguage.responseUploadMediaID !== "undefined"
  ) {
    templateHeaderMedia = `,{
      "type" : "HEADER",
      "format" : "${headerFormat.toUpperCase()}", 
      "example":{"header_handle":["${workingLanguage.responseUploadMediaID}"]}
    }`;
  }

  const templateFooter = `{
    "type" : "FOOTER",
    "text" : "${workingLanguage.footer}"
  }`;

  const templateFooterExpiration = `{
    "type" : "FOOTER",
    "code_expiration_minutes" : ${workingLanguage.code_expiration_minutes}
  }`

  const templateCTA = `{
    "type" : "BUTTONS",
    "buttons" : [<<button>>]
  }`;

  const templateButtons = `{
    "type" : "<<type>>",
    <<otpType>>
    <<text>>
    <<url>>
    <<package_name>>
    <<signature_hash>>
    <<zero_tap_terms_accepted>>
    <<example>>
    <<phonenumber>>
    
  }`;

  // Determine if this is an OTP authentication template
  const isOTPTemplate = template.category?.toLowerCase() === "authentication" && 
                        (workingLanguage.addSecurityRecommendation || workingLanguage.code_expiration_minutes);

  let templateBody = `{
    "type" : "BODY",
    "text" : "${workingLanguage.body?.replaceAll("\n", "\\n")}"
    <<example>>
  }`;

  // For OTP authentication templates, use auto-generated body (never include custom text)
  if (workingLanguage.addSecurityRecommendation) {
    console.log(`[Template Request] Creating OTP template with security recommendation - auto-generated body`);
    templateBody = `{
      "type" : "BODY",
      "add_security_recommendation" : true
    }`;
  } else if (isOTPTemplate) {
    console.log(`[Template Request] Creating OTP template with expiration only - minimal body`);
    // OTP template without security recommendation (e.g., with only expiration)
    templateBody = `{
      "type" : "BODY"
    }`;
  }
  let jsonRequestCreateMessage = `
  {
    "name" : "${template.templateName}",
    "language" : "${language.language}",
    "category" : "${template.category?.toUpperCase()}",
    "components" : [${templateBody}<<templateheader>><<templatefooter>><<templateCTA>>]
  }`;

  let jsonRequestUpdateMessage = `
  {
    "components" : [${templateBody}<<templateheader>><<templatefooter>><<templateCTA>>]
  }`;


  let jsonMessage = "";
  if (isUpdateStage) {
    jsonMessage = jsonRequestUpdateMessage;
  } else {
    jsonMessage = jsonRequestCreateMessage;
  }

  /* Check Footer */
  if (workingLanguage.code_expiration_minutes) {
    console.log(`[Template Request] Adding footer with code expiration: ${workingLanguage.code_expiration_minutes} minutes`);
    jsonMessage = jsonMessage.replace(
      "<<templatefooter>>",
      "," + templateFooterExpiration
    );
  }

  if (workingLanguage.footer && workingLanguage.footer !== "") {
    console.log(`[Template Request] Adding custom footer text: ${workingLanguage.footer}`);
    jsonMessage = jsonMessage.replace(
      "<<templatefooter>>",
      "," + templateFooter
    );
  }
  
  
  jsonMessage = jsonMessage.replace("<<templatefooter>>", "");
  

    
  //Process CTA
  if (workingLanguage.buttons && workingLanguage.buttons.length > 0) {
    console.log(`[Template Request] Processing ${workingLanguage.buttons.length} buttons`);
    let buttons = "";

    /**
     Function used to determine the order of the elements.
     It is expected to return a negative value if the first argument is less than the second argument, 
     zero if they're equal, and 
     a positive value otherwise. If omitted, the elements are sorted in ascending, ASCII character order.
     */

    const btns =  [...workingLanguage.buttons].sort((a,b)=>{
      if (b.type!==a.type) {
        if (a.type==="QUICK_REPLY")
          return -1
        
        if (b.type==="QUICK_REPLY") 
          return 1
      }

      return 0
    })

    for (let index = 0; index < btns.length; index++) {
      const element = btns[index];
      let tempButton = templateButtons;
      tempButton = tempButton
        .replace("<<type>>", element.type);

      tempButton = tempButton
        .replace("<<otpType>>", element.otpType? `"otp_type" : "${element.otpType}",` : "");
        
      tempButton = tempButton
        .replace("<<text>>", element.text? `"text" : "${element.text}"` : "");
      
      const package_name = element.packageName? `"package_name":"${element.packageName}",` : ""
      tempButton = tempButton
        .replace("<<package_name>>", package_name);
      
      const signature_hash = element.signaturehash? `"signature_hash":"${element.signaturehash}",` : ""
      tempButton = tempButton
        .replace("<<signature_hash>>", signature_hash);

      const zero_tap_terms_accepted = element.termsAccepted? `"zero_tap_terms_accepted":${element.termsAccepted},` : ""
      tempButton = tempButton
        .replace("<<zero_tap_terms_accepted>>", zero_tap_terms_accepted);
      
      if (element.url && element.url !== "" && element.urlType === "DYNAMIC" && element.urlDynamic) {
        const dynamicURLExample = element.dynamicValue ? element.urlDynamic?.replace("{{1}}", element.dynamicValue) : "url-example";
        tempButton = tempButton.replace("<<url>>", `,"url":"${element.urlDynamic}"`);
        tempButton = tempButton.replace(
          "<<example>>",
          `,"example":["${dynamicURLExample}"]`
        );
      }else if (element.url && element.url !== "") {
        tempButton = tempButton.replace("<<url>>", `,"url":"${element.url}"`);
        tempButton = tempButton.replace("<<example>>", "");
      } else {
        tempButton = tempButton.replace("<<url>>", "");
        tempButton = tempButton.replace("<<example>>", "");
      }

      if (element.phoneNumber && element.phoneNumber !== "") {
        tempButton = tempButton.replace(
          "<<phonenumber>>",
          `,"phone_number":"${element.phoneNumber}"`
        );
      } else {
        tempButton = tempButton.replace("<<phonenumber>>", "");
      }

      if (index === language.buttons.length - 1) {
        buttons += tempButton;
      } else {
        buttons += tempButton + ",";
      }
    }
    jsonMessage = jsonMessage.replace(
      "<<templateCTA>>",
      "," + templateCTA.replace("<<button>>", buttons)
    );
  } else {
    jsonMessage = jsonMessage.replace("<<templateCTA>>", "");
  }
  console.log("Check Button : " + jsonMessage);

  if (workingLanguage.bodyParam && workingLanguage.bodyParam?.length > 0) {
    console.log(`[Template Request] Adding body parameters: ${workingLanguage.bodyParam.length} parameters`);
    //Generate Example
    let arrBodyParam: string = "";
    for (let index = 1; index <= workingLanguage.bodyParam.length; index++) {
      if (index == workingLanguage.bodyParam.length) {
        arrBodyParam += `"body-example-${index}"`;
      } else {
        arrBodyParam += `"body-example-${index}",`;
      }
    }
    jsonMessage = jsonMessage.replace(
      "<<example>>",
      `,"example" : {
      "body_text": [[${arrBodyParam}]]
    }`
    );
  } else {
    jsonMessage = jsonMessage.replace("<<example>>", "");
  }

  {
    /* Check header */
  }
  if (headerFormat && headerFormat !== "") {
    if (headerFormat === "text") {
      let headerParameters = language.header?.headerParam;
      if (headerParameters && headerParameters.length > 0) {
        //Generate Example
        let arrHeaderParam: string = "";
        for (let index = 1; index <= headerParameters.length; index++) {
          if (index == headerParameters.length) {
            arrHeaderParam += `"header-example-${index}"`;
          } else {
            arrHeaderParam += `"header-example-${index}",`;
          }
        }
        templateHeaderText = templateHeaderText.replace(
          "<<example>>",
          `,"example" : {
          "header_text": [${arrHeaderParam}]
        }`
        );
      } else {
        templateHeaderText = templateHeaderText.replace("<<example>>", "");
      }
      jsonMessage = jsonMessage.replace(
        "<<templateheader>>",
        templateHeaderText
      );
    } else {
      jsonMessage = jsonMessage.replace(
        "<<templateheader>>",
        templateHeaderMedia
      );
    }
  } else {
    jsonMessage = jsonMessage.replace("<<templateheader>>", "");
  }

  console.log(`[Template Request] Final JSON message generated:`, jsonMessage);
  console.log(`[Template Request] About to send request to server`);
  console.log(`[Template Request] Request type: ${isUpdateStage ? 'UPDATE' : 'CREATE'}`);
  Log.debug("sendRequestCreateTemplate - JSON Message :" + jsonMessage);

  
  if (isUpdateStage) {
    console.log(`[Template Request] Calling sendUpdateTemplate`);
    Log.debug("do update template");
    
    return await sendUpdateTemplate(
      jsonMessage,
      whatsappBusinessID,
      whatsappAccessToken,
      templateID,
      (
        responseStatus: boolean,
        responseJson: JSON,
        errorMessage: string,
        errorJSON: JSON
      ) => {
        console.log(`[Template Request] Update response received - Status: ${responseStatus}`);
        console.log(`[Template Request] Update response JSON:`, responseJson);
        console.log(`[Template Request] Update error message: ${errorMessage}`);
        console.log(`[Template Request] Update error JSON:`, errorJSON);
        callback(responseStatus, responseJson, errorMessage, errorJSON);
      }
    );
  } else {
    console.log(`[Template Request] Calling sendCreateTemplate`);
    Log.debug("do create template");
    return await sendCreateTemplate(
      jsonMessage,
      whatsappBusinessID,
      whatsappAccessToken,
      (
        responseTemplateID: string,
        responseJson: JSON,
        errorMessage: string,
        errorJSON: JSON,
        accountData: any
      ) => {
        console.log(`[Template Request] Create response received - Template ID: ${responseTemplateID}`);
        console.log(`[Template Request] Create response JSON:`, responseJson);
        console.log(`[Template Request] Create error message: ${errorMessage}`);
        console.log(`[Template Request] Create error JSON:`, errorJSON);
        console.log(`[Template Request] Account data from server:`, accountData);
        Log.debug(`wabaId:${whatsappBusinessID}, errorMessage:${errorMessage}`)
        callback(responseTemplateID, responseJson, errorMessage, errorJSON, accountData);
      }
    );
  }
  
};
